import { REPORTS_ENDPOINT, REPORT_QUERY_PARAMS } from "@sscs/report/constants";
import type { ReportRequest, ReportSummaryRequest } from "@sscs/report/report";
import { ReportMappings, ReportSummaryData, ScanReportType } from "@sscs/types";
import { COMMUNITY_ENDPOINT, PROJECTS_ENDPOINT } from "Constants";
import type { RootState } from "Store/store";
import {
  CommunityPackage,
  CommunityPackageRequest,
  GroupUpdatesMessage,
  LatestReport,
  LatestReportRequest,
  Version,
} from "Types";
import { baseApi } from "./base.api";
import { updateSummaryApiCache } from "./reportWebsocketUpdates";
import { listenToGroupUpdates } from "./websocket";

export const sdlcReportApi = baseApi.injectEndpoints({
  endpoints: (build) => ({
    getLatestReport: build.query<LatestReport, LatestReportRequest>({
      query: (data) => {
        const queryParams = data.build
          ? `?${REPORT_QUERY_PARAMS.build}=${data.build}`
          : "";

        return {
          url: "reports/v1/latest-report",
          params: {
            group_id: data.groupId,
            purl: `pkg:rl/${data.projectName}/${data.packageName}@${data.version}${queryParams}`,
            diff_with: data.diffWith,
          },
        };
      },
      providesTags: ["LatestReport"],
      async onCacheEntryAdded(
        arg,
        { updateCachedData, cacheDataLoaded, cacheEntryRemoved },
      ) {
        const listener = (event: MessageEvent) => {
          const message = JSON.parse(event.data) as GroupUpdatesMessage;

          if (message.entity === "version" && message.action === "updated") {
            updateCachedData((draft) => {
              const updatedVersion = message.data.find(
                (item) => item.version_id === draft.file_id,
              );
              if (!updatedVersion) return draft;

              const reportType: ScanReportType =
                arg.build === "repro" ? "repro" : "version";

              return {
                ...draft,
                shared_reports_stats:
                  updatedVersion.shared_reports_stats[reportType],
              };
            });
          }
        };

        await listenToGroupUpdates(
          cacheDataLoaded,
          cacheEntryRemoved,
          listener,
        );
      },
    }),
    getPurlFromVersion: build.query<Version, { versionId: string }>({
      query: (queryArg) => ({
        url: `${PROJECTS_ENDPOINT}/versions/${queryArg.versionId}`,
      }),
    }),
    getReportMappings: build.query<ReportMappings, void>({
      query: () => ({
        url: `/reports/v1/report-mappings`,
      }),
    }),

    getReportSummary: build.query<
      ReportSummaryData,
      ReportRequest<ReportSummaryRequest>
    >({
      query: (data) => ({
        url: `${REPORTS_ENDPOINT}/${data.ui_id}/summary`,
        params: data,
      }),
      async onCacheEntryAdded(
        arg,
        {
          updateCachedData,
          cacheDataLoaded,
          cacheEntryRemoved,
          getState,
          dispatch,
        },
      ) {
        const listener = (event: MessageEvent) => {
          const data = JSON.parse(event.data) as GroupUpdatesMessage;
          const state = getState() as RootState;

          updateCachedData((draft) =>
            updateSummaryApiCache(dispatch, state, draft, data, arg),
          );
        };

        await listenToGroupUpdates(
          cacheDataLoaded,
          cacheEntryRemoved,
          listener,
        );
      },
    }),
    getCommunityPackage: build.query<CommunityPackage, CommunityPackageRequest>(
      {
        query: (queryArg) => ({
          url: `${COMMUNITY_ENDPOINT}/report/package/${queryArg.purl}`,
        }),
      },
    ),
  }),
  overrideExisting: true,
});

export const {
  useGetLatestReportQuery,
  useGetPurlFromVersionQuery,
  useGetReportMappingsQuery,
  useGetReportSummaryQuery,
  useGetCommunityPackageQuery,
} = sdlcReportApi;
